import React from 'react';
import PropTypes from 'prop-types';
import { Card, Spinner, EmptyState, Plus } from '@opendate-app/setlist';
import { formatCompactMoney } from '../../shared/utils/crmMoney';
import crmLabels from '../../shared/utils/crmLabels';

// ponytail: categorical data-viz palette for pipeline-stage segments; distinct hues, not theme chrome — intentionally not tokenized
const OPEN_COLORS = ['#B2DDFF', '#84CAFF', '#53B1FD', '#2E90FA', '#D9D6FE', '#BDB4FE', '#9B8AFB', '#7A5AF8', '#FEDF89', '#FEC84B', '#FDB022', '#F79009'];
const WON_COLOR = '#32D583';

const colorFor = (row, index) => (row.stage === 'closed_won' ? WON_COLOR : OPEN_COLORS[index % OPEN_COLORS.length]);

function DealPipeline({ pipeline, teamId, loading, labels = crmLabels(undefined) }) {
  const dealSingularLower = labels.deal.singular.toLowerCase();
  const dealPluralLower = labels.deal.plural.toLowerCase();
  const rows = pipeline || [];
  const open = rows.filter((r) => r.stage !== 'closed_won');
  const openValue = open.reduce((s, r) => s + Number(r.value || 0), 0);
  const openCount = open.reduce((s, r) => s + Number(r.count || 0), 0);
  const totalValue = rows.reduce((s, r) => s + Number(r.value || 0), 0) || 1;
  const hasDeals = rows.some((r) => Number(r.count) > 0);

  return (
    <Card padding="md">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <strong>Pipeline</strong>
        <span className="text-muted small">
          {hasDeals ? `${openCount} open · ${formatCompactMoney(openValue)}` : '$0 open'}
        </span>
      </div>

      {loading && <div className="d-flex justify-content-center py-4"><Spinner label="Loading…" /></div>}

      {!loading && !hasDeals && (
        <EmptyState
          size="sm"
          title={`No ${dealPluralLower} yet`}
          description={`Track sponsorships and other sales activity here. Create your first ${dealSingularLower} to start a pipeline.`}
          primaryAction={{ label: `New ${dealSingularLower}`, icon: Plus, onClick: () => { window.location.href = `/teams/${teamId}/crm/deals?action=new`; } }}
        />
      )}

      {!loading && hasDeals && (
        <>
          <div className="crm-dash__pipeline-bar">
            {rows.map((r, i) => (
              <div key={r.stage} style={{ width: `${(Number(r.value || 0) / totalValue) * 100}%`, background: colorFor(r, i) }} />
            ))}
          </div>
          <div className="crm-dash__pipeline-legend">
            {rows.map((r, i) => (
              <div key={r.stage}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: colorFor(r, i), marginRight: 6 }} />
                <span className="text-muted small">{r.label}{r.stage === 'closed_won' ? ' (this month)' : ''}</span>
                <div style={{ fontWeight: 600 }}>{formatCompactMoney(r.value)}</div>
                <div className="text-muted small">{r.count} {(Number(r.count) === 1 ? labels.deal.singular : labels.deal.plural).toLowerCase()}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

DealPipeline.propTypes = {
  pipeline: PropTypes.arrayOf(PropTypes.object),
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loading: PropTypes.bool,
  labels: PropTypes.object,
};

export default DealPipeline;
